import type { ReactNode } from "react";

export type AlertKind = "error" | "success" | "info";

const TONE: Record<AlertKind, string> = {
  error: "border-danger bg-danger/10 text-danger",
  success: "border-verified bg-verified-bg text-verified",
  info: "border-line bg-surface-2 text-ink",
};
const ICON: Record<AlertKind, string> = { error: "⚠", success: "✓", info: "ℹ" };

/**
 * A prominent, color-coded status banner. Sticks to the top of the scroll area so it stays in
 * view no matter where the triggering action was on the page (errors must never scroll out of
 * sight). `role="alert"` + `aria-live` announce it to screen readers.
 */
export function Alert({ kind, children, onDismiss }: { kind: AlertKind; children: ReactNode; onDismiss?: () => void }) {
  return (
    <div
      role="alert"
      aria-live={kind === "error" ? "assertive" : "polite"}
      className={`sticky top-2 z-20 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm font-medium shadow-sm ${TONE[kind]}`}
    >
      <span className="flex items-start gap-2">
        <span aria-hidden className="mt-px font-bold">{ICON[kind]}</span>
        <span>{children}</span>
      </span>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0 opacity-70 hover:opacity-100">✕</button>
      )}
    </div>
  );
}
